import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import FAQ from '../components/ui/FAQ';
import CTASection from '../components/ui/CTASection';
import { contactContent } from '../content/contact';
import { openWhatsAppConsultation } from '../utils/whatsapp';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    budget: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, this would send data to a backend
    alert('شكراً لتواصلك! سنرد عليك قريباً');
    setFormData({
      fullName: '',
      email: '',
      phone: '',
      service: '',
      message: '',
      budget: ''
    });
  };

  return (
    <>
      <Helmet>
        <title>اتصل بنا - نمو ماركتينج</title>
        <meta
          name="description"
          content="تواصل مع نمو ماركتينج - نحن هنا لمساعدتك. احصل على استشارة مجانية عبر واتساب أو املأ نموذج التواصل"
        />
      </Helmet>

      <div className="contact-page">
        {/* Hero Section */}
        <section className="page-hero">
          <div className="container">
            <div className="page-hero-content">
              <h1 className="page-hero-title">{contactContent.hero.title}</h1>
              <p className="page-hero-subtitle">{contactContent.hero.subtitle}</p>
              <p className="page-hero-description">{contactContent.hero.description}</p>
            </div>
          </div>
        </section>

        {/* Contact Methods */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{contactContent.contactMethods.title}</h2>
            </div>
            <div className="grid grid-4">
              {contactContent.contactMethods.methods.map((method) => {
                const MethodIcon = method.icon;
                return (
                  <div key={method.id} className={`contact-method-card ${method.isPrimary ? 'primary' : ''}`}>
                    <div className="contact-method-icon">
                      <MethodIcon />
                    </div>
                    <h3 className="contact-method-title">{method.title}</h3>
                    {method.link ? (
                      <a href={method.link} className="contact-method-value" target="_blank" rel="noopener noreferrer">
                        {method.value}
                      </a>
                    ) : (
                      <p className="contact-method-value">{method.value}</p>
                    )}
                    <p className="contact-method-description">{method.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="section bg-light">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{contactContent.contactForm.title}</h2>
              <p className="section-subtitle">{contactContent.contactForm.subtitle}</p>
            </div>
            <div className="contact-form-container">
              <form onSubmit={handleSubmit} className="contact-form">
                {contactContent.contactForm.fields.map((field) => {
                  if (field.type === 'select') {
                    return (
                      <div key={field.name} className="form-group">
                        <label htmlFor={field.name}>
                          {field.label}
                          {field.required && <span className="required">*</span>}
                        </label>
                        <select
                          id={field.name}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          required={field.required}
                          className="form-control"
                        >
                          {field.options.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }

                  if (field.type === 'textarea') {
                    return (
                      <div key={field.name} className="form-group">
                        <label htmlFor={field.name}>
                          {field.label}
                          {field.required && <span className="required">*</span>}
                        </label>
                        <textarea
                          id={field.name}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          placeholder={field.placeholder}
                          required={field.required}
                          rows={field.rows || 4}
                          className="form-control"
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={field.name} className="form-group">
                      <label htmlFor={field.name}>
                        {field.label}
                        {field.required && <span className="required">*</span>}
                      </label>
                      <input
                        type={field.type}
                        id={field.name}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        required={field.required}
                        className="form-control"
                      />
                    </div>
                  );
                })}

                <button type="submit" className="btn btn-primary btn-large btn-full-width">
                  {contactContent.contactForm.submitButton}
                </button>
              </form>

              <div className="contact-whatsapp-alternative">
                <p>أو تواصل معنا مباشرة عبر واتساب</p>
                <button
                  onClick={openWhatsAppConsultation}
                  className="btn btn-whatsapp btn-large"
                >
                  تواصل عبر واتساب 💬
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Working Hours */}
        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">{contactContent.workingHours.title}</h2>
            </div>
            <div className="working-hours-card">
              {contactContent.workingHours.schedule.map((schedule, index) => (
                <div key={index} className="working-hours-item">
                  <span className="working-hours-days">{schedule.days}</span>
                  <span className="working-hours-time">{schedule.hours}</span>
                  {schedule.timezone && (
                    <span className="working-hours-timezone">{schedule.timezone}</span>
                  )}
                </div>
              ))}
              {contactContent.workingHours.note && (
                <p className="working-hours-note">{contactContent.workingHours.note}</p>
              )}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ title={contactContent.faq.title} items={contactContent.faq.items} />

        {/* Final CTA */}
        <CTASection
          title={contactContent.cta.title}
          subtitle={contactContent.cta.subtitle}
          buttonText={contactContent.cta.buttonText}
          backgroundStyle="gradient"
        />
      </div>
    </>
  );
};

export default Contact;
